import { useRef } from "react";
import { Global } from "@emotion/react";
import { v4 } from "uuid";

import {
  EXPORTABLES_PREVIEW_FORM_ID,
  EXPORTABLES_PREVIEW_PRINTING_CLASSNAME,
} from "@/components/AIComponents/ExportablesModal/ExportablesModal.constants";
import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections";
import { type TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { type SourceAnalysisOrganizerFormData } from "@/types/models/Exportables.interface";
import {
  containerStyles,
  guideRowsContainer,
  topSeparatorStyles,
} from "./SourceAnalysisOrganizer.styles";
import SourceAnalysisRow from "./SourceAnalysisRow";
import { createRowsData } from "./utils";

const TEXT_AREA_DEFAULT_HEIGHT = "63px";

const SourceAnalysisOrganizer = ({
  formData,
  onChange,
  title = "Source analysis",
  isStudent = false,
}: TemplateProps<SourceAnalysisOrganizerFormData>) => {
  const uniqueId = useRef(v4()).current;
  const data = formData as SourceAnalysisOrganizerFormData;
  const directionsText =
    "Read your primary source once. Then, read it a second time and take notes on the questions below. Finally answer the conclusion questions.";
  const { directions = directionsText } = data;
  const articleTitle = data.article_title || data.articleShortTitle || "";

  const rowsData = createRowsData();

  return (
    <>
      <div css={containerStyles}>
        <ExportablesTemplateHeaderSection
          templateTitle={title}
          directions={
            <OrganizerDirections
              name="Primary source analysis"
              value={directions}
              handleChange={(e) => {
                onChange?.({ directions: e.target.value });
              }}
              isStudent={isStudent}
            />
          }
          articleTitle={articleTitle}
        />
        <div css={guideRowsContainer}>
          <div css={topSeparatorStyles} />
          {rowsData.map((row) => (
            <SourceAnalysisRow
              key={`${row.field}-${uniqueId}`}
              field={row.field}
              label={row.text}
              onChange={(e) => {
                onChange?.({ [row.field]: e.target.value });
              }}
              templateId={uniqueId}
              value={data[row.field] || ""}
            />
          ))}
        </div>
      </div>
      <Global
        styles={{
          [`#${EXPORTABLES_PREVIEW_FORM_ID}:not(.${EXPORTABLES_PREVIEW_PRINTING_CLASSNAME}) textarea[data-qa-selector^="exportables-source-analysis"]`]:
            {
              height: TEXT_AREA_DEFAULT_HEIGHT,
              minHeight: TEXT_AREA_DEFAULT_HEIGHT,
              maxHeight: TEXT_AREA_DEFAULT_HEIGHT,
            },
        }}
      />
    </>
  );
};

export default SourceAnalysisOrganizer;
